/**
 * Pure helpers for the Step 6 disbursement table (sc-45812). No axios, no store —
 * everything here is deterministic and unit-tested so the table stays a thin view.
 *
 * Responsibilities:
 *  - normalize backend enum casing (PascalCase | camelCase) into FE unions
 *  - fold the read-path response into a flat, ordered list of view-model rows
 *  - build the request refs for submit/undo
 *  - map per-row submit/undo results back onto stable row keys
 */
import type {
  DisbursementRow,
  DisbursementRowRef,
  DisbursementRowResult,
  DisbursementRowType,
  DisbursementsData,
  RawDisbursementRowResult,
  RawDisbursementsResponse,
  RawFirmInvoiceRow,
  RawLienRow,
  RawClientNetRow,
  RawCoCounselRow,
  PayabliRowView,
} from '../models/disbursementTypes';
import { roleLabel } from './roleLabels';

// ── Stable row keys ──────────────────────────────────────────────────────────

export const FIRM_ROW_KEY = 'firm';
export const CLIENT_ROW_KEY = 'client';
export const lienRowKey = (lienId: string): string => `lien:${lienId}`;
export const feeRecipientRowKey = (recipientId: string): string => `feeRecipient:${recipientId}`;

/** Stable key for a row ref — used to correlate refs, results, and view rows. */
export const rowKeyForRef = (ref: DisbursementRowRef): string => {
  if (ref.type === 'firmInvoice') return FIRM_ROW_KEY;
  if (ref.type === 'clientNet') return CLIENT_ROW_KEY;
  if (ref.type === 'feeRecipient') return feeRecipientRowKey(ref.recipientId ?? '');
  return lienRowKey(ref.lienId ?? '');
};

// ── Enum normalization ───────────────────────────────────────────────────────

/**
 * Normalize the *submit/undo* row type (backend DisbursementRowType) to the FE
 * union, case-insensitively. Returns null for unrecognized values.
 */
export const normalizeRowType = (
  raw: string | null | undefined,
): DisbursementRowType | null => {
  switch ((raw ?? '').toLowerCase()) {
    case 'firminvoice':
    case 'invoicepayment': // read-path discriminator maps onto the same submit ref
      return 'firmInvoice';
    case 'lien':
      return 'lien';
    case 'clientnet':
    case 'clientnetrecovery':
      return 'clientNet';
    case 'feerecipient':
      return 'feeRecipient';
    default:
      return null;
  }
};

/** The backend enum spelling to send in a submit/undo request body. */
export const toApiRowType = (type: DisbursementRowType): string => {
  switch (type) {
    case 'firmInvoice':
      return 'FirmInvoice';
    case 'clientNet':
      return 'ClientNet';
    case 'lien':
      return 'Lien';
    case 'feeRecipient':
      return 'FeeRecipient';
  }
};

/**
 * Human label for a lien's LienType ("Medical Lien" / "Subrogation Claim" …).
 *
 * TODO(backend): this display copy should come from the API as a `typeLabel`
 * field (like `blockedReason`/`pushErrorMessage` already do) so there's a single
 * source of truth and no FE/BE drift. Kept here as a temporary adapter until the
 * read path returns it; delete this map when it does.
 */
export const lienTypeLabel = (lienType: string | null | undefined): string => {
  switch ((lienType ?? '').toLowerCase()) {
    case 'medicalprovider':
    case 'medical':
      return 'Medical Lien';
    case 'insurancesubrogation':
    case 'subrogation':
      return 'Subrogation Claim';
    case 'governmentmedicare':
      return 'Government / Medicare';
    case 'letterofprotection':
      return 'Letter of Protection';
    case 'governmentlien':
    case 'government':
      return 'Government Lien';
    case 'attorneylien':
      return 'Attorney Lien';
    case 'childsupport':
      return 'Child Support Lien';
    default:
      return 'Lien';
  }
};

// ── Blocked-reason copy ──────────────────────────────────────────────────────
//
// The read DTO gives us booleans (isOrphaned, vendorMissingQboId) but no message,
// so the frontend authors the user-facing copy for these conditions. Kept together
// (not inlined in the mappers) so the wording is easy to review and update, and so
// it's obvious these strings mirror specific backend row flags.
export const BLOCKED_REASON = {
  orphanedLien:
    'This lien is no longer part of the settlement snapshot and cannot be pushed.',
  vendorMissingQboId:
    'This lien holder is not linked to a QuickBooks vendor. Link the vendor before pushing.',
  firmSagaRecovery:
    'The invoice was finalized but paying from trust could not be completed or reversed. Resolve this in the billing workflow before continuing.',
} as const;

/**
 * Fallback copy for a submitted row's disabled Undo, used only when the backend
 * didn't send a row-specific `undoBlockedReason` (defensive — the read path
 * always populates it per sc-46091). Shown as a tooltip on the disabled Undo
 * button; prefer `row.undoBlockedReason` over this whenever it's set.
 */
export const undoBlockedReasonFallback = (rowKey: string): string =>
  rowKey === FIRM_ROW_KEY
    ? 'Undo the client net disbursement first, then this can be reversed.'
    : 'This disbursement cannot be undone right now.';

// ── Row folding ──────────────────────────────────────────────────────────────

const firmToRow = (raw: RawFirmInvoiceRow): DisbursementRow => {
  let status: DisbursementRow['status'];
  let message: string | null = null;
  if (raw.sagaErrorState) {
    status = 'needs-recovery';
    message = raw.sagaErrorMessage || BLOCKED_REASON.firmSagaRecovery;
  } else if (raw.submitted) {
    status = 'done';
  } else if (raw.canSubmit) {
    status = 'pending';
  } else {
    status = 'blocked';
    message = raw.blockedReason;
  }
  return {
    key: FIRM_ROW_KEY,
    ref: { type: 'firmInvoice' },
    recipientName: raw.recipientName || 'Firm Invoice',
    recipientKind: 'Firm',
    typeLabel: 'Invoice Payment',
    amount: raw.amount,
    feeAmount: null,
    expenseAmount: null,
    percentage: null,
    status,
    // A saga error leaves the invoice created but unpaid — retry re-attempts the
    // trust payment only, so canSubmit stays whatever the backend reports.
    canSubmit: raw.canSubmit,
    canUndo: raw.canUndo,
    // A row is selectable when it can be acted on: submittable (bulk Submit) OR
    // reversible (bulk Undo). The backend canSubmit/canUndo flags encode the
    // invoice <-> client-net ordering, so this stays correct across states.
    selectable: raw.canSubmit || raw.canUndo,
    message,
    undoBlockedReason: raw.undoBlockedReason ?? null,
  };
};

/**
 * Derive a lien row's status + message. These are prioritized, mutually-exclusive
 * conditions (order matters), so early returns read cleaner than an if/else-if
 * chain or a `switch (true)`. First match wins.
 */
const lienStatus = (
  raw: RawLienRow,
): { status: DisbursementRow['status']; message: string | null } => {
  if (raw.isOrphaned)
    return { status: 'blocked', message: BLOCKED_REASON.orphanedLien };
  if (raw.qboTransactionId) return { status: 'done', message: null };
  if (raw.pushErrorMessage)
    return { status: 'error', message: raw.pushErrorMessage };
  if (raw.vendorMissingQboId)
    return { status: 'blocked', message: BLOCKED_REASON.vendorMissingQboId };
  if (raw.canSubmit) return { status: 'pending', message: null };
  return { status: 'blocked', message: null };
};

const lienToRow = (raw: RawLienRow): DisbursementRow => {
  const { status, message } = lienStatus(raw);
  return {
    key: lienRowKey(raw.lienId),
    ref: { type: 'lien', lienId: raw.lienId },
    recipientName: raw.recipientName || 'Lien Holder',
    recipientKind: 'Lien Holder',
    typeLabel: lienTypeLabel(raw.lienType),
    amount: raw.amount,
    feeAmount: null,
    expenseAmount: null,
    percentage: null,
    status,
    canSubmit: raw.canSubmit,
    canUndo: raw.canUndo,
    // A row is selectable when it can be acted on: submittable (bulk Submit) OR
    // reversible (bulk Undo). The backend canSubmit/canUndo flags encode the
    // invoice <-> client-net ordering, so this stays correct across states.
    selectable: raw.canSubmit || raw.canUndo,
    message,
    undoBlockedReason: raw.undoBlockedReason ?? null,
  };
};

const toPayabliView = (raw: RawClientNetRow): PayabliRowView | null =>
  raw.payabli
    ? {
        disbursementId: raw.payabli.disbursementId ?? null,
        state: raw.payabli.state,
        amount: raw.payabli.amount,
        actions: raw.payabli.actions ?? [],
        cancelBlockedReason: raw.payabli.cancelBlockedReason ?? null,
        emailDeliveryPending: raw.payabli.emailDeliveryPending ?? false,
      }
    : null;

const clientToRow = (raw: RawClientNetRow): DisbursementRow => {
  let status: DisbursementRow['status'];
  let message: string | null = null;
  if (raw.submitted) {
    status = 'done';
  } else if (raw.canSubmit) {
    status = 'pending';
  } else {
    status = 'blocked';
    message = raw.blockedReason;
  }
  return {
    key: CLIENT_ROW_KEY,
    ref: { type: 'clientNet' },
    recipientName: raw.recipientName || 'Client',
    recipientKind: 'Client',
    typeLabel: 'Client Net Recovery',
    amount: raw.amount,
    feeAmount: null,
    expenseAmount: null,
    percentage: null,
    status,
    canSubmit: raw.canSubmit,
    canUndo: raw.canUndo,
    // A row is selectable when it can be acted on: submittable (bulk Submit) OR
    // reversible (bulk Undo). The backend canSubmit/canUndo flags encode the
    // invoice <-> client-net ordering, so this stays correct across states.
    selectable: raw.canSubmit || raw.canUndo,
    message,
    undoBlockedReason: raw.undoBlockedReason ?? null,
    payabli: toPayabliView(raw),
    canSend: raw.canSend ?? false,
    sendBlockedReason: raw.sendBlockedReason ?? null,
  };
};

const feeRecipientToRow = (raw: RawCoCounselRow): DisbursementRow => {
  let status: DisbursementRow['status'];
  let message: string | null = null;
  if (raw.submitted) {
    status = 'done';
  } else if (raw.canSubmit) {
    status = 'pending';
  } else {
    status = 'blocked';
    message = raw.blockedReason;
  }
  const label = roleLabel(raw.role);
  // 'other' isn't a role covered by the co-counsel/referring-attorney fee-split feature (BR-002) —
  // it already skips the " Fee" suffix, so it skips the +Expenses/breakdown treatment too.
  const hasExpenses = raw.role !== 'other' && raw.expenseAmount > 0;
  return {
    key: feeRecipientRowKey(raw.feeRecipientId),
    ref: { type: 'feeRecipient', recipientId: raw.feeRecipientId },
    recipientName: raw.recipientName || label,
    recipientKind: label,
    typeLabel:
      raw.role === 'other' ? label : `${label} Fee${hasExpenses ? ' + Expenses' : ''}`,
    amount: raw.amount,
    feeAmount: raw.role === 'other' ? null : raw.feeAmount,
    expenseAmount: raw.role === 'other' ? null : raw.expenseAmount,
    percentage: raw.role === 'other' ? null : raw.percentage,
    status,
    canSubmit: raw.canSubmit,
    canUndo: raw.canUndo,
    selectable: raw.canSubmit || raw.canUndo,
    message,
    undoBlockedReason: raw.undoBlockedReason ?? null,
  };
};

/**
 * Fold the raw read-path response into the flat, ordered view model.
 * Row order mirrors the mockup: Firm invoice, Client net, lien holders, then fee
 * recipients (co-counsel / referring attorney).
 */
export const normalizeDisbursements = (
  raw: RawDisbursementsResponse | null | undefined,
): DisbursementsData => {
  const payabliEnabled = !!raw?.payabliEnabled;
  const clientRow = raw?.clientNetRow ? clientToRow(raw.clientNetRow) : null;

  const rows: DisbursementRow[] = [];
  if (raw?.firmInvoiceRow) rows.push(firmToRow(raw.firmInvoiceRow));
  // Lifted out of the bulk table for a flagged firm: the client is paid through Send, so the row
  // must not carry a checkbox or count toward select-all. The backend agrees — it forces
  // canSubmit false and drops this row from canSubmitAll — but keeping it in `rows` would still
  // render a dead checkbox and a second place to act on the same money.
  if (clientRow && !payabliEnabled) rows.push(clientRow);
  (raw?.lienRows ?? []).forEach((lien) => rows.push(lienToRow(lien)));
  (raw?.coCounselRows ?? []).forEach((r) => rows.push(feeRecipientToRow(r)));

  const flags = raw?.bulkFlags;
  // clientNetRow is null when net recovery <= 0 — surface as a negative-net warning.
  const clientNetNegative = !!raw && raw.clientNetRow == null;

  return {
    rows,
    canSubmitAll: !!flags?.canSubmitAll,
    allDisbursed: !!flags?.allDisbursed,
    anySubmitted: !!flags?.anyRowSubmitted,
    clientNetNegative,
    payabliEnabled,
    payabliConnected: !!raw?.payabliConnected,
    trustBalance: raw?.trustBalance ?? null,
    clientRow: payabliEnabled ? clientRow : null,
  };
};

// ── Submit / undo refs + results ─────────────────────────────────────────────

/** Map the FE refs into the request body the backend expects. */
export const toSubmitRequestBody = (
  refs: DisbursementRowRef[],
): { rows: Array<{ type: string; lienId?: string; recipientId?: string }> } => ({
  rows: refs.map((ref) => {
    if (ref.type === 'lien') return { type: toApiRowType(ref.type), lienId: ref.lienId };
    if (ref.type === 'feeRecipient') {
      return { type: toApiRowType(ref.type), recipientId: ref.recipientId };
    }
    return { type: toApiRowType(ref.type) };
  }),
});

/** Stable row key for an already-normalized type. Null when a lien/fee-recipient ref has no id. */
const keyForType = (
  type: DisbursementRowType,
  lienId: string | null | undefined,
  recipientId: string | null | undefined,
): string | null => {
  if (type === 'lien') return lienId ? lienRowKey(lienId) : null;
  if (type === 'feeRecipient') return recipientId ? feeRecipientRowKey(recipientId) : null;
  return type === 'firmInvoice' ? FIRM_ROW_KEY : CLIENT_ROW_KEY;
};

/** Map a single raw result back onto a stable row key. Null if the type is unknown. */
export const mapResultToKey = (
  raw: RawDisbursementRowResult,
): string | null => {
  const type = normalizeRowType(raw.type);
  return type ? keyForType(type, raw.lienId, raw.recipientId) : null;
};

/** Normalize the per-row results array, dropping any row whose type/id can't be resolved. */
export const normalizeResults = (
  raw: RawDisbursementRowResult[] | null | undefined,
): DisbursementRowResult[] =>
  (raw ?? [])
    .map((r): DisbursementRowResult | null => {
      const type = normalizeRowType(r.type);
      const key = type && keyForType(type, r.lienId, r.recipientId);
      if (!type || !key) return null;
      return {
        key,
        type,
        lienId: r.lienId ?? null,
        recipientId: r.recipientId ?? null,
        success: !!r.success,
        errorMessage: r.errorMessage ?? null,
      };
    })
    .filter((r): r is DisbursementRowResult => r !== null);
